import { useState } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFavorites } from "@/lib/favorites-context";
import { ApiError } from "@/lib/api";

// 130 -- favourite/unfavourite a school. Persists to followed_school_ids; the
// "My Favourites" events chip reads the same context. Two presentations:
//   variant="button" -- labelled outline button (school detail header)
//   variant="icon"   -- compact star (table rows)

interface FavoriteSchoolButtonProps {
  schoolId: string;
  schoolName?: string;
  variant?: "button" | "icon";
}

export function FavoriteSchoolButton({
  schoolId,
  schoolName,
  variant = "button",
}: FavoriteSchoolButtonProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const favorite = isFavorite(schoolId);
  const label = favorite ? "Remove from favourites" : "Add to favourites";
  const title = schoolName
    ? favorite
      ? `Unfollow ${schoolName}`
      : `Follow ${schoolName}`
    : label;

  async function handleToggle() {
    setLoading(true);
    setError(null);
    try {
      await toggleFavorite(schoolId);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to update favourite");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  }

  if (variant === "icon") {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleToggle}
        disabled={loading}
        aria-label={label}
        aria-pressed={favorite}
        title={title}
        data-favorite={favorite ? "true" : "false"}
        data-action="toggle-favorite"
      >
        <Star className={`h-4 w-4 ${favorite ? "fill-current text-amber-500" : "text-muted-foreground"}`} />
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={handleToggle}
        disabled={loading}
        aria-pressed={favorite}
        title={title}
        data-favorite={favorite ? "true" : "false"}
        data-action="toggle-favorite"
      >
        <Star className={`h-3.5 w-3.5 ${favorite ? "fill-current text-amber-500" : ""}`} />
        <span className="hidden sm:inline">{favorite ? "Favourited" : "Favourite"}</span>
      </Button>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}
